/**
 * FileTransferCard — one row on the File destination.
 *
 * Presentational only: no state, no platform imports, no transport. The host
 * owns the transfer machine and hands this a resolved `state`.
 *
 * ONE component covers both directions, matching the Figma, which models
 * outgoing (preparing / waiting-approval / transferring) and incoming
 * (offered / transferring) as variants of a single `FileCard` rather than two
 * components. Direction is not a prop: it is implied by which state the host
 * passes and which callbacks it wires.
 *
 * SECURITY: `fileName` is E2E-adjacent — it must never be logged or sent to
 * analytics. This component renders it and nothing else does anything with it.
 * Privacy mode does not mask this card; the host unmounts the whole
 * destination instead (see the spec's G5).
 */
import { File04, Server01, XClose, Zap } from "@untitledui/icons";
import { IconButton } from "../IconButton";
import { ProgressBar } from "../ProgressBar";
import { Text } from "../Text";
import styles from "./FileTransferCard.module.css";

/**
 * The states the card can render.
 *
 * `preparing` and `transferring` carry their own percentage because the two
 * come from different sources — hashing progress is byte-accurate, transfer
 * progress is derived from completed parts — and the card should not have to
 * know which. A null `pct` on `preparing` means the byte counters were not
 * available and the status reads without a number.
 */
export type FileTransferState =
  | { kind: "preparing"; pct: number | null }
  /**
   * `needsAppOpen` marks a recipient that cannot be woken by push (the foreground tier), so the
   * offer sits unseen until Reflecto is opened on it. The plain wait says "any second now"; this
   * one needs the user to go and do something, which is the only actionable difference the card
   * can express here.
   */
  | { kind: "waiting-approval"; needsAppOpen?: boolean }
  | { kind: "transferring"; pct: number }
  /** Incoming offer awaiting the user's answer. The only state with no dismiss affordance. */
  | { kind: "offered" }
  | { kind: "done" }
  | { kind: "cancelled" }
  /**
   * The offer's 1h server TTL elapsed before anyone answered. Distinct from
   * `failed` because nothing went wrong — `client-core`'s transfer status
   * models it separately too — so it takes the muted tone rather than the
   * negative one.
   */
  | { kind: "expired" }
  /** `message` is host-resolved: an FtErrorCode string, or one of the send-failure reasons. */
  | { kind: "failed"; message: string };

export interface FileTransferCardLabels {
  /** `pct` is null when the byte counters aren't available yet. */
  preparing: (pct: number | null) => string;
  waitingApproval: string;
  /** Shown instead of [waitingApproval] when the recipient cannot be woken — an instruction, not a status. */
  waitingApprovalAppOpen: string;
  /** `title` on that instruction, explaining why this wait is different. */
  waitingApprovalAppOpenHint: string;
  transferring: (pct: number) => string;
  done: string;
  cancelled: string;
  expired: string;
  accept: string;
  reject: string;
  /** aria-label for the × while a transfer is live. */
  cancel: string;
  /** aria-label for the × on a finished transfer — it clears the row, it doesn't stop anything. */
  dismiss: string;
  /**
   * How the bytes are travelling. BOTH paths are end-to-end encrypted — the
   * relay stores ciphertext it cannot read — so the hints must not imply the
   * server path is less private. It is a speed/route distinction, not a
   * security one.
   */
  transportDirect: string;
  /** `title` on the direct chip. */
  transportDirectHint: string;
  transportRelay: string;
  /** `title` on the relay chip. Reassure, do not alarm. */
  transportRelayHint: string;
  /** aria-label for the progress bar. */
  progress: string;
}

export interface FileTransferCardProps {
  fileName: string;
  /** Pre-formatted by the host — byte formatting is locale-dependent. */
  fileSizeLabel: string;
  state: FileTransferState;
  labels: FileTransferCardLabels;
  /** Cancels a live transfer, or dismisses a finished one. Omit to hide the ×. */
  onCancel?: () => void;
  /**
   * Incoming offers only. Same rule as `onCancel`: an unwired handler hides its
   * button rather than rendering a dead one. A `kind: "offered"` row with
   * neither handler therefore shows no action row at all — which is visibly
   * wrong, and so surfaces the wiring mistake instead of swallowing clicks.
   */
  onAccept?: () => void;
  onReject?: () => void;
  /**
   * Which route the bytes are taking. `undefined` until the transport is known
   * (it is negotiated after the transfer starts), and the chip is omitted then
   * rather than guessing — a wrong route claim is worse than none.
   */
  transport?: "direct" | "relay";
}

/**
 * Terminal states show no progress bar and swap the × from "cancel" to
 * "dismiss". Written as an exhaustive switch rather than an `||` chain so a
 * new state cannot silently inherit the live-transfer treatment.
 */
function isTerminal(state: FileTransferState): boolean {
  switch (state.kind) {
    case "done":
    case "cancelled":
    case "expired":
    case "failed":
      return true;
    case "preparing":
    case "waiting-approval":
    case "transferring":
    case "offered":
      return false;
  }
}

function statusText(state: FileTransferState, labels: FileTransferCardLabels): string | null {
  switch (state.kind) {
    case "preparing":
      return labels.preparing(state.pct === null ? null : normalisePct(state.pct));
    case "waiting-approval":
      return state.needsAppOpen ? labels.waitingApprovalAppOpen : labels.waitingApproval;
    case "transferring":
      return labels.transferring(normalisePct(state.pct));
    case "done":
      return labels.done;
    case "cancelled":
      return labels.cancelled;
    case "expired":
      return labels.expired;
    case "failed":
      return state.message;
    // An un-answered offer shows no status — the Accept/Reject row IS the status.
    case "offered":
      return null;
  }
}

/**
 * Normalise before anything renders it. Progress is derived arithmetic at every
 * call site, so a host bug producing 140 must not make the label read "140%"
 * beside a bar that ProgressBar has capped at 100 — and a fractional 33.7 must
 * not read "33.7%" while the bar announces 34.
 */
function normalisePct(pct: number): number {
  if (!Number.isFinite(pct)) return 0;
  return Math.round(Math.min(100, Math.max(0, pct)));
}

/** Only the two in-flight states have a determinate bar. */
function progressPct(state: FileTransferState): number | null {
  if (state.kind === "transferring") return normalisePct(state.pct);
  if (state.kind === "preparing") return state.pct === null ? null : normalisePct(state.pct);
  return null;
}

/**
 * Exhaustive for the same reason as `isTerminal` — a `default` here would let
 * a future terminal state render in the live-transfer accent.
 */
function statusTone(state: FileTransferState): "accent" | "muted" | "negative" {
  switch (state.kind) {
    case "failed":
    case "cancelled":
      return "negative";
    // Nothing went wrong; the row is just history.
    case "done":
    case "expired":
      return "muted";
    case "preparing":
    case "waiting-approval":
    case "transferring":
    case "offered":
      return "accent";
  }
}

export function FileTransferCard({
  fileName,
  fileSizeLabel,
  state,
  labels,
  onCancel,
  onAccept,
  onReject,
  transport,
}: FileTransferCardProps) {
  const status = statusText(state, labels);
  const pct = progressPct(state);
  const offered = state.kind === "offered";

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <div className={styles.iconBox} aria-hidden="true">
          <File04 className={styles.fileIcon} />
        </div>

        <div className={styles.meta}>
          {/* `title` so a truncated name is still recoverable on hover. */}
          <p className={styles.fileName} title={fileName}>
            {fileName}
          </p>
          <div className={styles.metaRow}>
            <Text size="xs" tone="tertiary">
              {fileSizeLabel}
            </Text>
            {status !== null && (
              // `role="status"` matches SendFileCard/ReceiveFileCard, which
              // announce every status line today. Without it, delegating those
              // cards to this one in a later slice would silently drop the
              // "Sent" / failure announcement for screen-reader users.
              // Percentage ticks do get announced, but they arrive per ~5 MiB
              // part rather than per chunk, so the rate is tolerable.
              <span
                role="status"
                className={`${styles.status} ${styles[statusTone(state)]}`}
                // Same pattern as the transport chip below: the "why" rides a
                // title so it costs no width in a card this narrow.
                title={
                  state.kind === "waiting-approval" && state.needsAppOpen
                    ? labels.waitingApprovalAppOpenHint
                    : undefined
                }
              >
                {status}
              </span>
            )}
            {/* The design has a single badge slot, but route and progress are
                orthogonal — a transfer is "33%" AND direct. Rendered as its own
                chip so neither has to win the slot. Icon + text rather than text
                alone: at 12px uppercase the two labels scan slowly, and the
                glyph carries the distinction at a glance. Both this chip and the
                status stay short enough to share one line — see .metaRow. */}
            {transport === "direct" && (
              <span
                className={`${styles.transport} ${styles.transportDirect}`}
                title={labels.transportDirectHint}
              >
                <Zap className={styles.transportIcon} aria-hidden="true" />
                {labels.transportDirect}
              </span>
            )}
            {transport === "relay" && (
              <span className={styles.transport} title={labels.transportRelayHint}>
                <Server01 className={styles.transportIcon} aria-hidden="true" />
                {labels.transportRelay}
              </span>
            )}
          </div>
        </div>

        {/* An un-answered offer has no ×: declining is an explicit choice via
            Reject, not a dismissal. */}
        {onCancel && !offered && (
          <IconButton
            icon={XClose}
            size="s"
            label={isTerminal(state) ? labels.dismiss : labels.cancel}
            onClick={onCancel}
          />
        )}
      </div>

      {pct !== null && <ProgressBar value={pct} label={labels.progress} />}

      {offered && (onAccept || onReject) && (
        <div className={styles.actions}>
          {/* Accept is primary: the design styles both buttons identically, which
              leaves the affirmative action with no emphasis and makes Reject
              equally easy to hit by accident. */}
          {onAccept && (
            <button type="button" className={styles.accept} onClick={onAccept}>
              {labels.accept}
            </button>
          )}
          {onReject && (
            <button type="button" className={styles.reject} onClick={onReject}>
              {labels.reject}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
